/**
 * Stores all the info for elements that capture focus.
 * Each item in the scopes array will have the following properties:
 *
 * @HTMLElement  ancestor               default: document.documentElement
 *      A ancestor/scope node that contains a child node that captures the focus.
 *
 *      Only descendants in the last node in children array will be selected/valid for focus
 *      No ancestor's descendants nodes will also be selected/valid for focus !!!
 *
 * @Array       children
 *      Stack of ancestor descendants nodes.
 *      Each node in the array is a focus prison, but only the last one is considered/used for each ancestor/scope
 */

const globalInfo = {
  scopes: []
};

/**
 * Selector that matches almost all valid focusable elements in the DOM
 */
const focusQuery =
  'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

export function addIndexDirectionListener() {
  const isTabDown = e => {
    if (e.key === 'Tab') {
      const isReverseIndexation = e.shiftKey;
      if (isReverseIndexation === undefined || globalInfo.scopes.length === 0) {
        return;
      }

      /**
       * Get all current (i.e. rendered) focusable elements in the DOM.
       * Apply filter to remove remaining non focusable elements. In this way, the focusQuery remains simple
       */
      let focusableElements = [...document.querySelectorAll(focusQuery)].filter(
        el =>
          !el.hasAttribute('disabled') &&
          el.getAttribute('tabindex') !== '-1' &&
          el.getAttribute('type') !== 'hidden'
      );
      if (isReverseIndexation) {
        focusableElements.reverse();
      }

      /**
       * Sort the focusable elements in a way in which the current focus (e.target) ends in the last position.
       * The next to the current focus ends in the first position.
       */
      const index = focusableElements.indexOf(e.target);
      focusableElements = focusableElements
        .slice(index + 1, focusableElements.length)
        .concat(focusableElements.slice(0, index + 1));

      /**
       * Search for the next focusable element inside the ancestor node
       *
       */
      const nextNode = focusableElements.find(
        node =>
          !globalInfo.scopes.some(
            item =>
              item.ancestor.contains(node) &&
              !item.children[item.children.length - 1].contains(node)
          )
      );
      nextNode && nextNode.focus();
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };
  document.addEventListener('keydown', isTabDown, true);
}

export const captureFocusUtilities = function() {
  const utilities = {
    lastFocusOutsideModal: null,
    scope: null,
    ancestor: null,
    anyCaptureApplied: false
  };

  utilities.capture = function(
    focusableChild,
    ancestor = document.documentElement,
    autoFocus = true
  ) {
    this.focusableChild = focusableChild;
    this.lastFocusOutsideModal = document.activeElement;
    this.ancestor = ancestor;
    const repeated = globalInfo.scopes.find(item => item.ancestor === ancestor);
    if (repeated) {
      repeated.children.push(focusableChild);
    } else {
      globalInfo.scopes.push({
        ancestor,
        children: [focusableChild]
      });
    }
    if (autoFocus) {
      setTimeout(() => {
        focusableChild.querySelector(focusQuery).focus();
      }, 500);
    }
    utilities.anyCaptureApplied = true;
  }.bind(utilities);

  utilities.free = function(focusPreviousElement = true) {
    if (this.ancestor) {
      globalInfo.scopes = globalInfo.scopes.filter(item => {
        if (item.ancestor === utilities.ancestor) {
          item.children = item.children.filter(
            child => child !== utilities.focusableChild
          );
          return item.children.length > 0;
        }
        return true;
      });
    }
    if (
      focusPreviousElement &&
      this.lastFocusOutsideModal &&
      this.lastFocusOutsideModal.focus
    ) {
      setTimeout(() => {
        this.lastFocusOutsideModal.focus();
      }, 100);
    }
  }.bind(utilities);

  return utilities;
};



// WEBPACK FOOTER //
// ./src/utils/adaConfigurations/ModalFocusPrision.js